// Push Notification Manager for J-Style Services
// Handles push subscriptions, permission requests, and sound alerts

const VAPID_PUBLIC_KEY = 'BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkrxZJjSgSnfckjBJuBkr3qBUYIHBQFLXYp5Nksh8U';

// Notification sound URLs (using Web Audio API compatible sounds)
const NOTIFICATION_SOUNDS = {
  ride_request: 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3', // Alert tone
  driver_assigned: 'https://assets.mixkit.co/active_storage/sfx/2867/2867-preview.mp3', // Success tone
  driver_arrived: 'https://assets.mixkit.co/active_storage/sfx/2870/2870-preview.mp3', // Arrival chime
  message: 'https://assets.mixkit.co/active_storage/sfx/2354/2354-preview.mp3', // Message tone
  default: 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3'
};

class NotificationManager {
  constructor() {
    this.swRegistration = null;
    this.subscription = null;
    this.audioContext = null;
    this.soundBuffers = {};
    this.isSupported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  }

  // Initialize the notification manager
  async init() {
    if (!this.isSupported) {
      console.warn('Push notifications not supported in this browser');
      return false;
    }

    try {
      // Register service worker
      this.swRegistration = await navigator.serviceWorker.register('/service-worker.js');
      console.log('Service Worker registered:', this.swRegistration);

      // Wait for the service worker to be ready
      await navigator.serviceWorker.ready;

      // Preload notification sounds
      this.preloadSounds();

      return true;
    } catch (error) {
      console.error('Failed to initialize notifications:', error);
      return false;
    }
  }

  // Request notification permission
  async requestPermission() {
    if (!this.isSupported) return false;

    try {
      const permission = await Notification.requestPermission();
      console.log('Notification permission:', permission);
      return permission === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }

  // Subscribe to push notifications
  async subscribe(userId, userType = 'user') {
    if (!this.isSupported || !this.swRegistration) {
      console.warn('Cannot subscribe: notifications not initialized');
      return null;
    }

    try {
      // Check permission
      const permission = await this.requestPermission();
      if (!permission) {
        console.warn('Notification permission denied');
        return null;
      }

      // Get existing subscription or create new one
      let subscription = await this.swRegistration.pushManager.getSubscription();
      
      if (!subscription) {
        // Create new subscription
        subscription = await this.swRegistration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
        });
      }

      this.subscription = subscription;

      // Send subscription to backend
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      await fetch(`${backendUrl}/api/notifications/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          user_type: userType,
          subscription: subscription.toJSON()
        })
      });

      console.log('Push subscription successful:', subscription);
      return subscription;

    } catch (error) {
      console.error('Failed to subscribe to push notifications:', error);
      return null;
    }
  }

  // Unsubscribe from push notifications
  async unsubscribe(userId, userType = 'user') {
    if (!this.subscription) return;

    try {
      await this.subscription.unsubscribe();
      
      // Notify backend
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      await fetch(`${backendUrl}/api/notifications/unsubscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          user_type: userType
        })
      });

      this.subscription = null;
      console.log('Unsubscribed from push notifications');

    } catch (error) {
      console.error('Failed to unsubscribe:', error);
    }
  }

  // Preload notification sounds
  preloadSounds() {
    if (!window.AudioContext && !window.webkitAudioContext) {
      console.warn('Web Audio API not supported');
      return;
    }

    this.audioContext = new (window.AudioContext || window.webkitAudioContext)();

    Object.entries(NOTIFICATION_SOUNDS).forEach(([key, url]) => {
      fetch(url)
        .then(response => response.arrayBuffer())
        .then(arrayBuffer => this.audioContext.decodeAudioData(arrayBuffer))
        .then(audioBuffer => {
          this.soundBuffers[key] = audioBuffer;
          console.log(`Sound loaded: ${key}`);
        })
        .catch(error => console.warn(`Failed to load sound ${key}:`, error));
    });
  }

  // Play notification sound
  playSound(type = 'default') {
    // Try Web Audio API first
    if (this.audioContext && this.soundBuffers[type]) {
      try {
        // Resume audio context if suspended (required for iOS)
        if (this.audioContext.state === 'suspended') {
          this.audioContext.resume();
        }

        const source = this.audioContext.createBufferSource();
        source.buffer = this.soundBuffers[type];
        source.connect(this.audioContext.destination);
        source.start(0);
        return;
      } catch (error) {
        console.warn('Web Audio playback failed:', error);
      }
    }

    // Fallback to HTML5 Audio
    const soundUrl = NOTIFICATION_SOUNDS[type] || NOTIFICATION_SOUNDS.default;
    const audio = new Audio(soundUrl);
    audio.volume = 1.0;
    audio.play().catch(error => {
      console.warn('Audio playback failed:', error);
    });
  }

  // Show local notification (when app is in foreground)
  async showLocalNotification(title, body, options = {}) {
    // Play sound
    this.playSound(options.type || 'default');

    // Vibrate if supported
    if ('vibrate' in navigator) {
      navigator.vibrate([300, 100, 300, 100, 300]);
    }

    // Check permission
    if (Notification.permission !== 'granted') {
      console.warn('Notification permission not granted');
      return;
    }

    // Use service worker to show notification (works better on mobile)
    if (this.swRegistration) {
      try {
        await this.swRegistration.showNotification(title, {
          body: body,
          icon: 'https://customer-assets.emergentagent.com/job_transport-app-29/artifacts/c2qih6ut_J%20%26%20S%20Logo%2001%20%20%282%29_1024x1024.jpg',
          badge: 'https://customer-assets.emergentagent.com/job_transport-app-29/artifacts/c2qih6ut_J%20%26%20S%20Logo%2001%20%20%282%29_1024x1024.jpg',
          tag: options.tag || 'jstyle-local-' + Date.now(),
          renotify: true,
          requireInteraction: options.requireInteraction || false,
          vibrate: [300, 100, 300, 100, 300],
          data: options.data || {}
        });
      } catch (error) {
        console.error('Failed to show notification:', error);
      }
    }
  }

  // Helper: Convert VAPID key
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  // Check if notifications are supported and enabled
  getStatus() {
    return {
      supported: this.isSupported,
      permission: 'Notification' in window ? Notification.permission : 'denied',
      subscribed: !!this.subscription
    };
  }
}

// Create singleton instance
const notificationManager = new NotificationManager();

export default notificationManager;
export { NOTIFICATION_SOUNDS };
